import React, { useMemo, useState } from 'react';
import { parseLocalDate } from '../../utils/dateUtils';
import { ScheduleItem } from '../../api/schedules';
import { getHolidaysForYearRange } from '../../utils/holidayUtils';
import { getCalendarColor } from './CalendarsList';
import { CampFormatUtils } from '../../utils/campFormatUtils';
import { Camp } from '../../utils/types';
import CampDetails from '../camps/CampDetails';
import { Calendar as CalendarIcon, Tent, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import './ScheduleSummary.css';

interface ScheduleSummaryProps {
  schedules: ScheduleItem[];
  selectedScheduleIds: string[];
  onClose: () => void;
  hasSharedSchedules?: boolean;
}

interface GroupedItem {
  type: 'camp' | 'custom' | 'holiday';
  id: string;
  title: string;
  startDate: Date;
  endDate: Date;
  price?: number;
  campUrl?: string;
  emoji?: string;
  color?: string;
  camp?: Camp;
}

interface MonthGroup {
  month: string;
  year: number;
  items: GroupedItem[];
  monthlyCost: number;
}

const ScheduleSummary: React.FC<ScheduleSummaryProps> = ({
  schedules,
  selectedScheduleIds,
  onClose,
  hasSharedSchedules = false
}) => {
  // State for tracking the start date for the summary view
  const [startDate, setStartDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  // State for managing the CampDetails modal
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null);

  const { groupedItems, totalCost } = useMemo(() => {
    const items: GroupedItem[] = [];
    let cost = 0;

    // Calculate one year from the start date for filtering
    const oneYearFromStart = new Date(startDate);
    oneYearFromStart.setFullYear(oneYearFromStart.getFullYear() + 1);

    // Add scheduled camps and custom events
    schedules.forEach((schedule, index) => {
      if (!selectedScheduleIds.includes(schedule.uuid)) return;

      const color = getCalendarColor(index);

      schedule.scheduled_camps?.forEach(scheduled => {
        const campStartDate = parseLocalDate(scheduled.camp_date.start_date);
        const campEndDate = parseLocalDate(scheduled.camp_date.end_date);

        // Filter: only include camps starting from start date and within one year
        if (campStartDate >= startDate && campStartDate <= oneYearFromStart) {
          const price = scheduled.camp.price || 0;

          items.push({
            type: 'camp',
            id: `camp-${schedule.uuid}-${scheduled.camp.id}-${scheduled.camp_date.id}`,
            title: scheduled.camp.name,
            startDate: campStartDate,
            endDate: campEndDate,
            price,
            campUrl: scheduled.camp.website || undefined,
            emoji: '🏕️',
            color,
            camp: scheduled.camp
          });

          cost += price;
        }
      });

      // Add custom events
      schedule.custom_events?.forEach(event => {
        const eventStartDate = parseLocalDate(event.start_date);
        const eventEndDate = parseLocalDate(event.end_date);

        // Filter: only include events starting from start date and within one year
        if (eventStartDate >= startDate && eventStartDate <= oneYearFromStart) {
          items.push({
            type: 'custom',
            id: `custom-${schedule.uuid}-${event.id}`,
            title: event.title,
            startDate: eventStartDate,
            endDate: eventEndDate,
            emoji: '📅',
            color
          });
        }
      });
    });

    // Add holidays
    const startYear = startDate.getFullYear();
    const endYear = oneYearFromStart.getFullYear();
    const holidays = getHolidaysForYearRange(startYear, endYear);
    holidays.forEach(holiday => {
      // Filter: only include holidays from start date and within one year
      if (holiday.date >= startDate && holiday.date <= oneYearFromStart) {
        items.push({
          type: 'holiday',
          id: `holiday-${holiday.title}-${holiday.date.toISOString()}`,
          title: holiday.title,
          startDate: holiday.date,
          endDate: holiday.date,
          emoji: holiday.emoji,
          color: '#FF6B6B' // Use a red color for holidays
        });
      }
    });

    // Sort items by date
    items.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

    // Group by month
    const grouped: MonthGroup[] = [];
    items.forEach(item => {
      const monthKey = `${item.startDate.getFullYear()}-${item.startDate.getMonth()}`;
      let group = grouped.find(g =>
        `${g.year}-${new Date(g.year, new Date(g.month + ' 1').getMonth()).getMonth()}` === monthKey
      );

      if (!group) {
        const monthName = item.startDate.toLocaleString('default', { month: 'long' });
        group = {
          month: monthName,
          year: item.startDate.getFullYear(),
          items: [],
          monthlyCost: 0
        };
        grouped.push(group);
      }

      group.items.push(item);

      // Add to monthly cost if this is a camp with a price
      if (item.type === 'camp' && item.price) {
        group.monthlyCost += item.price;
      }
    });

    return { groupedItems: grouped, totalCost: cost };
  }, [schedules, selectedScheduleIds, startDate]);

  // Navigation handlers
  const handlePreviousMonth = () => {
    setStartDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setStartDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const handleToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setStartDate(today);
  };

  const handleBookNow = (url?: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCampClick = (camp: Camp) => {
    setSelectedCamp(camp);
  };

  const formatDate = (startDate: Date, endDate: Date) => {
    // Check if same day
    if (startDate.toDateString() === endDate.toDateString()) {
      return startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }

    // Format as range
    const startMonth = startDate.toLocaleDateString('en-US', { month: 'short' });
    const endMonth = endDate.toLocaleDateString('en-US', { month: 'short' });
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    if (startMonth === endMonth) {
      return `${startMonth} ${startDay}-${endDay}`;
    }

    return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
  };

  return (
    <div className="schedule-summary">
      <div className="schedule-summary-header">
        <div className="schedule-summary-nav">
          <button className="schedule-summary-nav-btn" onClick={handlePreviousMonth} aria-label="Previous month">
            <ChevronLeft className="icon icon--sm" />
          </button>
          <button className="schedule-summary-nav-btn" onClick={handleNextMonth} aria-label="Next month">
            <ChevronRight className="icon icon--sm" />
          </button>
          <button className="schedule-summary-nav-btn schedule-summary-nav-btn--today" onClick={handleToday}>
            Today
          </button>
        </div>
        <button className="schedule-summary-nav-btn" onClick={onClose}>
          <span>Back to Calendar</span>
        </button>
      </div>

      <div className="schedule-summary-content">
        {groupedItems.map((group, groupIdx) => (
          <div key={`${group.month}-${group.year}`} className="schedule-summary-month">
            <h3 className="schedule-summary-month-title">
              {group.month} {group.year}
              {group.monthlyCost > 0 && (
                <span className="schedule-summary-month-cost">
                  {' — '}Total cost for {group.month}: ${group.monthlyCost.toLocaleString()}
                </span>
              )}
            </h3>

            <div className="schedule-summary-items">
              {group.items.map(item => (
                <div
                  key={item.id}
                  className={`schedule-summary-item schedule-summary-item--${item.type}`}
                  style={{ borderLeftColor: item.color }}
                >
                  <div className="schedule-summary-item-icon">
                    {item.emoji && <span className="item-emoji">{item.emoji}</span>}
                    {item.type === 'camp' && !item.emoji && <Tent className="icon icon--sm" />}
                    {item.type === 'custom' && !item.emoji && <CalendarIcon className="icon icon--sm" />}
                    {item.type === 'holiday' && !item.emoji && <Sparkles className="icon icon--sm" />}
                  </div>

                  <div className="schedule-summary-item-content">
                    <div className="schedule-summary-item-date">
                      {formatDate(item.startDate, item.endDate)}
                    </div>
                    <div className="schedule-summary-item-title">
                      {item.type === 'camp' && item.camp ? (
                        <button
                          className="schedule-summary-item-title-link"
                          onClick={() => handleCampClick(item.camp!)}
                        >
                          {item.title}
                        </button>
                      ) : (
                        item.title
                      )}
                    </div>
                  </div>

                  {item.type === 'camp' && item.camp && (
                    <>
                      <div className="schedule-summary-item-price">
                        {(() => {
                          const priceParts = CampFormatUtils.getPriceParts(item.camp);
                          return (
                            <>
                              <span>${priceParts.price}</span>
                              {priceParts.timeframe && <span className="camp-price-timeframe"> / {priceParts.timeframe}</span>}
                            </>
                          );
                        })()}
                      </div>
                      <button
                        className="schedule-summary-item-book-btn"
                        onClick={() => handleBookNow(item.campUrl)}
                        disabled={!item.campUrl}
                      >
                        Book Now
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {groupedItems.length === 0 && (
          <div className="schedule-summary-empty">
            <p>No scheduled events yet. Start adding camps to your calendar!</p>
          </div>
        )}
      </div>

      {selectedCamp && (
        <CampDetails
          camp={selectedCamp}
          isOpen={!!selectedCamp}
          onClose={() => setSelectedCamp(null)}
        />
      )}
    </div>
  );
};

export default ScheduleSummary;
